<template>
    <div style="display: contents;">
        <template v-if="buttonComponent == 'v-btn'">
            <v-tooltip top>
                <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                        v-on="{ ...tooltip }"
                        :fab="button.params.fab ? button.params.fab : false"
                        :icon="button.params.fab ? false : true"
                        :small="button.params.small ? true : false"
                        :color="button.params.color ? button.params.color : 'primary'"
                        @click="openDialog()"
                    >
                        <v-icon>{{ button.params.icon ? button.params.icon : 'mdi-barcode' }}</v-icon>
                    </v-btn>
                </template>
                <span>{{ button.params.tooltip ? button.params.tooltip : 'Stampa Barcode' }}</span>
            </v-tooltip>
        </template>

        <v-list-item v-else-if="buttonComponent == 'v-list-item'"
            dense 
            :disabled="disabled"
            @click.stop="openDialog()"
            ripple>
            <v-list-item-icon>
                <v-icon style="font-size: 18px">{{ button.params.icon ? button.params.icon : 'mdi-barcode' }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
                <v-list-item-title>{{ button.params.tooltip ? button.params.tooltip : 'Stampa Barcode' }}</v-list-item-title>
            </v-list-item-content>
        </v-list-item>

        <v-dialog
            v-model="dialog"
            :fullscreen="$vuetify.breakpoint.mdAndDown"
            width="600"
            persistent
            ref="barcodeDialog"
            eager>
            <v-card
                class="background-2"
                :class="{'': $vuetify.breakpoint.mdAndDown, 'round-card': $vuetify.breakpoint.lgAndUp}">
                <v-card-title class="pa-3">
                    <p class="pl-2 pl-xl-3">{{ button.params.tooltip ? button.params.tooltip : 'Stampa Barcode' }}</p>
                    <v-spacer></v-spacer>
                    <v-btn icon @click.native.stop="closeDialog()"><v-icon>close</v-icon></v-btn>
                </v-card-title>
                <v-divider></v-divider>

                <v-card-text class="pa-4">
                    <!-- Format Selection -->
                    <v-row class="mb-3">
                        <v-col cols="12">
                            <v-radio-group v-model="selectedFormat" row>
                                <v-radio
                                    label="PDF"
                                    value="pdf"
                                ></v-radio>
                                <v-radio
                                    label="ZPL (Zebra Printer)"
                                    value="zpl"
                                ></v-radio>
                            </v-radio-group>
                        </v-col>
                    </v-row>

                    <!-- Copies Selection -->
                    <v-row class="mb-3">
                        <v-col cols="12">
                            <v-text-field
                                v-model.number="copies"
                                label="Numero di copie"
                                type="number"
                                min="1"
                                max="100"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <!-- Zebra Printer Selection (only for ZPL) -->
                    <v-row v-if="selectedFormat === 'zpl'" class="mb-3">
                        <v-col cols="12">
                            <v-select
                                v-model="selectedDeviceId"
                                :items="zebraDevices"
                                item-text="name"
                                item-value="uid"
                                label="Stampante Zebra"
                                outlined
                                dense
                                :loading="loadingDevices"
                                @focus="discoverZebraDevices"
                            >
                                <template v-slot:append-outer>
                                    <v-btn
                                        v-if="loadingDevices"
                                        icon
                                        small
                                        @click="stopLoading"
                                        color="error"
                                    >
                                        <v-icon>mdi-stop</v-icon>
                                    </v-btn>
                                    <v-btn
                                        v-else
                                        icon
                                        small
                                        @click="discoverZebraDevices"
                                        color="primary"
                                    >
                                        <v-icon>mdi-refresh</v-icon>
                                    </v-btn>
                                </template>
                            </v-select>
                        </v-col>
                    </v-row>

                    <!-- Connection Status -->
                    <v-row v-if="selectedFormat === 'zpl'">
                        <v-col cols="12">
                            <v-alert
                                v-if="connectionError"
                                type="error"
                                dense
                                outlined
                                class="mb-3"
                            >
                                {{ connectionError }}
                            </v-alert>
                            <v-alert
                                v-else-if="zebraDevices.length === 0 && !loadingDevices"
                                type="warning"
                                dense
                                outlined
                                class="mb-3"
                            >
                                Nessuna stampante Zebra trovata. Assicurati che Zebra Browser Print sia installato e in esecuzione.
                                <br>
                                <small>Scarica da: <a href="https://www.zebra.com/us/en/software/printer-software/zebra-browser-print.html" target="_blank">zebra.com</a></small>
                            </v-alert>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-btn @click="closeDialog()" color="primary" text>Annulla</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn 
                        @click="printBarcode" 
                        :loading="printing" 
                        color="info" 
                        :disabled="selectedFormat === 'zpl' && !selectedDeviceId"
                    >
                        <v-icon left>mdi-printer</v-icon>
                        Stampa
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
export default {
    name: 'generate-barcode-dialog',
    props: {
        button: Object,
        buttonComponent: {type: String, default: 'v-btn'},
        model: Object,
        disabled: Boolean,
    },

    data() {
        return {
            dialog: false,
            printing: false,
            selectedFormat: 'pdf',
            selectedDeviceId: null,
            copies: 1,
            zebraDevices: [],
            loadingDevices: false,
            connectionError: null
        }
    },

    computed: {
        selectedDevice() {
            if (!this.selectedDeviceId) return null
            const deviceObj = this.zebraDevices.find(d => d.uid === this.selectedDeviceId)
            return deviceObj ? deviceObj.device : null
        }
    },

    methods: {
        openDialog() {
            this.dialog = true
            this.connectionError = null
            this.loadingDevices = false
            
            // If ZPL is selected, discover devices
            if (this.selectedFormat === 'zpl') {
                this.discoverZebraDevices()
            }
        },

        closeDialog() {
            this.dialog = false
            this.loadingDevices = false
            this.connectionError = null
        },

        discoverZebraDevices() {
            // Avoid multiple simultaneous discoveries
            if (this.loadingDevices) {
                return
            }

            if (typeof window.BrowserPrint === 'undefined') {
                this.connectionError = 'Zebra Browser Print non è installato o non è in esecuzione'
                this.loadingDevices = false
                return
            }

            this.loadingDevices = true
            this.zebraDevices = []
            this.connectionError = null

            // Set timeout to prevent infinite loading
            const timeout = setTimeout(() => {
                this.loadingDevices = false
                this.connectionError = 'Timeout nel rilevamento delle stampanti'
            }, 5000)

            // Get default device first
            window.BrowserPrint.getDefaultDevice("printer", (device) => {
                console.log('Default device found:', device)
                clearTimeout(timeout)
                this.selectedDeviceId = device.uid
                this.zebraDevices.push({
                    name: device.name + ' (Default)',
                    device: device,
                    uid: device.uid
                })

                // Discover other devices
                window.BrowserPrint.getLocalDevices((deviceList) => {
                    console.log('Additional devices found:', deviceList)
                    deviceList.forEach((device) => {
                        if (!this.selectedDeviceId || device.uid !== this.selectedDeviceId) {
                            this.zebraDevices.push({
                                name: device.name,
                                device: device,
                                uid: device.uid
                            })
                        }
                    })
                    this.loadingDevices = false
                    console.log('Device discovery completed')
                }, (error) => {
                    console.error('Error in getLocalDevices:', error)
                    this.connectionError = 'Errore nel rilevamento delle stampanti: ' + error
                    this.loadingDevices = false
                }, "printer")

            }, (error) => {
                console.error('Error in getDefaultDevice:', error)
                clearTimeout(timeout)
                this.connectionError = 'Errore di connessione: ' + error
                this.loadingDevices = false
            })
        },

        stopLoading() {
            this.loadingDevices = false
        },

        async printBarcode() {
            if (!this.model || !this.model.id) {
                this.$store.commit('setSnackbar', {
                    timeout: 6000,
                    text: 'Errore: Prodotto non valido',
                    value: true,
                    color: 'error'
                })
                return
            }

            this.printing = true

            try {
                if (this.selectedFormat === 'pdf') {
                    await this.printPDF()
                } else {
                    await this.printZPL()
                }
            } catch (error) {
                console.error('Errore stampa:', error)
                this.$store.commit('setSnackbar', {
                    timeout: 6000,
                    text: 'Errore durante la stampa: ' + error.message,
                    value: true,
                    color: 'error'
                })
            } finally {
                this.printing = false
            }
        },

        async printPDF() {
            const params = {
                format: 'pdf',
                copies: this.copies
            }

            const response = await this.axios.get(`products-barcode/${this.model.id}/print`, {
                params,
                responseType: 'blob'
            })

            // Create blob and download
            const blob = new Blob([response.data], { type: 'application/pdf' })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `Barcode_${this.model.code}_${this.copies}copie.pdf`
            link.click()
            window.URL.revokeObjectURL(url)

            this.$store.commit('setSnackbar', {
                timeout: 6000,
                text: `PDF generato con successo (${this.copies} ${this.copies === 1 ? 'copia' : 'copie'})`,
                value: true,
                color: 'success'
            })

            this.closeDialog()
        },

        async printZPL() {
            if (!this.selectedDevice) {
                throw new Error('Nessuna stampante selezionata')
            }

            const params = {
                copies: this.copies
            }

            try {
                const response = await this.axios.get(`products-barcode/${this.model.id}/zpl`, {
                    params
                })

                if (response.data.status === 'success') {
                    // Send ZPL to printer
                    this.selectedDevice.send(response.data.zpl, () => {
                        this.$store.commit('setSnackbar', {
                            timeout: 6000,
                            text: `Etichette stampate con successo (${this.copies} ${this.copies === 1 ? 'copia' : 'copie'})`,
                            value: true,
                            color: 'success'
                        })
                        this.closeDialog()
                    }, (error) => {
                        throw new Error('Errore di stampa: ' + error)
                    })
                } else {
                    throw new Error(response.data.message || 'Errore nella generazione ZPL')
                }
            } catch (error) {
                if (error.response && error.response.data && error.response.data.message) {
                    throw new Error(error.response.data.message)
                } else {
                    throw error
                }
            }
        }
    },

    watch: {
        selectedFormat(newVal) {
            if (newVal === 'zpl') {
                this.discoverZebraDevices()
            } else {
                // Reset loading state when switching to PDF
                this.loadingDevices = false
            }
        },
        
        dialog(newVal) {
            if (!newVal) {
                // Reset loading state when closing dialog
                this.loadingDevices = false
            }
        }
    },

    mounted() {
        // Load Zebra Browser Print library if not already loaded
        if (typeof window.BrowserPrint === 'undefined') {
            const script = document.createElement('script')
            script.src = '/zebra-browser-print-js-v31250/BrowserPrint-3.1.250.min.js'
            script.onload = () => {
                console.log('Zebra Browser Print library loaded')
            }
            script.onerror = () => {
                console.error('Failed to load Zebra Browser Print library')
                this.connectionError = 'Impossibile caricare la libreria Zebra Browser Print'
            }
            document.head.appendChild(script)
        }
    }
}
</script>

<style scoped>
.v-card {
    border-radius: 8px;
}
</style>
